import axios from 'axios'
import { usePatientStore } from '@/stores/patient'
import router from '@/router'

// Create axios instance with base configuration
export const api = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1/',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
})

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
    (config) => {
        // Skip auth for login/register endpoints
        const skipAuthUrls = ['/auth/patient-login', '/auth/register']
        if (skipAuthUrls.some((url) => config.url?.includes(url))) {
            return config
        }

        // Check for token in localStorage first (for page reloads)
        const storedToken = localStorage.getItem('patient_token')
        if (storedToken) {
            config.headers.Authorization = `Bearer ${storedToken}`
            return config
        }

        // Fallback to store token (for already authenticated sessions)
        const patientStore = usePatientStore()
        if (patientStore.isAuthenticated && patientStore.patient?.token) {
            config.headers.Authorization = `Bearer ${patientStore.patient.token}`
        }

        return config
    },
    (error) => {
        return Promise.reject(error)
    },
)

// Response interceptor - Handle auth errors
api.interceptors.response.use(
    (response) => {
        return response
    },
    async (error) => {
        const patientStore = usePatientStore()

        // Handle 401 Unauthorized - token expired or invalid
        if (error.response?.status === 401) {
            // Only logout if this was an authenticated request (not login attempts)
            const hasAuthHeader = error.config?.headers?.Authorization
            if (hasAuthHeader && !error.config?.url?.includes('/auth/patient-login')) {
                console.warn('Token expired or invalid, logging out')
                patientStore.logout()
                router.push('/login')
            }
        }

        // Handle 403 Forbidden - insufficient permissions
        if (error.response?.status === 403) {
            console.warn('Access denied:', error.response.data?.message)
        }

        return Promise.reject(error)
    },
)

export default api
